/**
 * 스마트 가이드라인 커스텀 훅
 * - 오브젝트 이동 시 다른 오브젝트/캔버스와의 정렬 보조선 표시
 * - before:transform에서 정렬 포인트 캐시
 * - object:moving에서 스냅 계산 + guides를 ref에 저장
 * - after:render에서 하위 캔버스에 가이드라인 렌더링
 */
import { useEffect, useRef } from 'react';
import useEditorStore from '../stores/useEditorStore';
import {
  collectAlignPoints,
  calculateSnap,
  renderGuideLines,
} from '../utils/alignmentGuides';

const useSmartGuides = (canvas) => {
  const alignPointsRef = useRef(null);
  const guidesRef = useRef([]);

  useEffect(() => {
    if (!canvas) return;

    const isDisabled = () => {
      const store = useEditorStore.getState();
      if (store.editingGroupId) return true;
      if (canvas.isDrawingMode) return true;
      const active = canvas.getActiveObject();
      if (active?.isEditing) return true;
      return false;
    };

    const handleBeforeTransform = (e) => {
      // 이동(drag)일 때만 가이드라인 활성화
      if (e.transform?.action !== 'drag') return;
      if (isDisabled()) return;

      const movingObj = e.transform.target;
      if (!movingObj) return;

      const canvasSize = useEditorStore.getState().canvasSize;
      const objects = canvas.getObjects();
      alignPointsRef.current = collectAlignPoints(objects, movingObj, canvasSize);
    };

    const handleObjectMoving = (e) => {
      if (!alignPointsRef.current) return;
      if (isDisabled()) {
        alignPointsRef.current = null;
        guidesRef.current = [];
        return;
      }

      const movingObj = e.target;
      if (!movingObj) return;

      const threshold = useEditorStore.getState().snapThreshold;
      const { deltaX, deltaY, guides } = calculateSnap(
        movingObj,
        alignPointsRef.current,
        threshold
      );

      // 스냅 위치 보정
      if (deltaX !== 0) movingObj.left += deltaX;
      if (deltaY !== 0) movingObj.top += deltaY;
      movingObj.setCoords();

      // guides를 ref에 저장 → after:render에서 렌더링
      guidesRef.current = guides;
    };

    // after:render: Fabric 렌더링 완료 후 하위 캔버스에 가이드라인 그리기
    const handleAfterRender = (e) => {
      const guides = guidesRef.current;
      if (!guides || guides.length === 0) return;

      const ctx = e?.ctx || canvas.contextContainer;
      if (!ctx) return;

      const vpt = canvas.viewportTransform;
      const thickness = useEditorStore.getState().guideThickness;
      renderGuideLines(ctx, guides, vpt, thickness);
    };

    const handleDragEnd = () => {
      if (guidesRef.current.length > 0 || alignPointsRef.current) {
        guidesRef.current = [];
        alignPointsRef.current = null;
        canvas.requestRenderAll();
      }
    };

    canvas.on('before:transform', handleBeforeTransform);
    canvas.on('object:moving', handleObjectMoving);
    canvas.on('after:render', handleAfterRender);
    canvas.on('object:modified', handleDragEnd);
    canvas.on('mouse:up', handleDragEnd);

    return () => {
      canvas.off('before:transform', handleBeforeTransform);
      canvas.off('object:moving', handleObjectMoving);
      canvas.off('after:render', handleAfterRender);
      canvas.off('object:modified', handleDragEnd);
      canvas.off('mouse:up', handleDragEnd);
    };
  }, [canvas]);
};

export default useSmartGuides;
